import { useEffect } from 'react';
import { X } from 'lucide-react';

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  showCloseButton = true,
  size = 'md',
  headerExtra = null,
}) {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    full: 'max-w-4xl',
    responsive: 'max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl',
    'full-height': 'max-w-2xl md:max-w-3xl lg:max-w-4xl xl:max-w-5xl',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`
          relative w-full ${sizeClasses[size]}
          bg-game-surface rounded-2xl border border-[#2a2d31]
          shadow-2xl animate-slide-up
          ${size === 'full-height' ? 'max-h-[95vh] min-h-[70vh]' : 'max-h-[90vh]'} flex flex-col my-auto
        `}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between p-5 border-b border-[#2a2d31] flex-shrink-0">
            {title && <h2 className="text-xl font-bold text-game-text">{title}</h2>}
            <div className="flex items-center gap-2">
              {headerExtra}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="p-2 rounded-xl hover:bg-game-secondary text-game-text-dim hover:text-game-text transition-colors"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-5 overflow-y-auto overflow-x-hidden flex-1 min-h-0">
          {children}
        </div>
      </div>
    </div>
  );
}
